import { createContext, useContext } from 'react';
import { useGetWhatsAppCampaignQuickReplies } from './hooks/useGetWhatsAppQuickReplies';

const QUICK_REPLY_BUTTON_TYPE = 'QuickReplyButton';

interface WhatsAppButton {
  buttonText: string;
  title: string;
  __typename: string;
}

interface LocaleContent {
  locale: {
    name: string;
    __typename: string;
  };
  buttons: WhatsAppButton[];
  __typename: string;
}

interface TemplateAndContent {
  primaryContent: LocaleContent;
  localeContents: LocaleContent[];
  __typename: string;
}

export const getLocaleButtonText = (data: { templateAndContent: TemplateAndContent }): Record<
  string,
  string[]
> => {
  const result: Record<string, string[]> = {};

  // Process primary content (usually 'en')
  const primaryLocale = data?.templateAndContent?.primaryContent || {};
  if (primaryLocale?.locale?.name) {
    result[primaryLocale.locale.name] =
      primaryLocale?.buttons
        ?.filter((button) => button?.__typename === QUICK_REPLY_BUTTON_TYPE)
        ?.map((button) => button?.buttonText.toLocaleLowerCase())
        ?.filter(Boolean) || [];
  }

  // Process other locales
  data?.templateAndContent?.localeContents?.forEach((content: LocaleContent) => {
    if (content?.locale?.name) {
      result[content.locale.name] =
        content?.buttons
          ?.filter((button) => button?.__typename === 'QuickReplyButton')
          ?.map((button) => button?.buttonText.toLocaleLowerCase())
          ?.filter(Boolean) || [];
    }
  });
  return result;
};

interface HoldForReplyContextState {
  quickReplies: Record<string, string[]>;
  fetchWhatsAppCampaignQuickReplies: (id: number) => void;
}

export const HoldUntilReplyContext = createContext<HoldForReplyContextState>({
  quickReplies: {},
  fetchWhatsAppCampaignQuickReplies: () => undefined,
});

export const HoldUntilReplyProvider = ({ children }: { children: React.ReactNode }) => {
  const { fetchWhatsAppCampaignQuickReplies, data } = useGetWhatsAppCampaignQuickReplies();

  const context: HoldForReplyContextState = {
    fetchWhatsAppCampaignQuickReplies,
    quickReplies: getLocaleButtonText(data),
  };
  return (
    <HoldUntilReplyContext.Provider value={context}>{children}</HoldUntilReplyContext.Provider>
  );
};

export const useHoldUntilReplyContext = () => useContext(HoldUntilReplyContext);
